import {
  AioExternalReplacements,
  AitRowGroupData,
  ARS,
  isAioExternalReplacements,
  isAioExternalSingle,
  isAitRowGroupData,
  isAnalysisMethod,
} from "aic";
import { AioExternalSingleWithSource } from "contexts/hierarchy/interface";
import { IEditorV3, isIEditorV3 } from "editor-v3";
import { CommentReadDto } from "../comments/interface";
import { isCommentReadDto } from "../comments/isCommentReadDto";
import { eHierarchy, eHierarchyLevel } from "../enums";
import { ClinicalLibraryReadDto } from "../library/interface";
import { isClinicalLibraryReadDto } from "../library/isClinicalLibraryReadDto";
import { HierarchyLink, isHierarchyLink } from "../tlfItem/TlfItemIndexReadDto";
import { DomainReplacements, isDomainReplacements } from "./DomainReplacements";
import { HierarchyChildReadDto, isHierarchyChildReadDto } from "./HierarchyChildReadDto";
import { HierarchyHistoryDto, isHierarchyHistoryDto } from "./HierarchyHistoryDto";
import { isITocSection, ITocSection } from "./ITocSection";
import {
  isTocMajorVersionDescription,
  TocMajorVersionDescription,
} from "./TocMajorVersionDescription";

export interface HierarchyReadDto {
  id: string;
  parentTag: string;
  name: string;
  type: eHierarchy;
  parentId: string;
  taPrefix: string;
  projectCode: string;
  dNumber: string;
  description: string;
  lists: AioExternalReplacements<IEditorV3>[];
  domainReplacements: DomainReplacements[];
  phase?: string;
  hierarchyTag: string;
  rowGroups: AitRowGroupData<IEditorV3>[];
  reportType?: string;
  sections: ITocSection[];
  singles: AioExternalSingleWithSource<IEditorV3>[];
  status?: string;
  studyStandardsDate?: string;
  domainList: string[];
  level: eHierarchyLevel;
  children: HierarchyChildReadDto[];
  history: HierarchyHistoryDto[];
  tocMajorVersion?: number;
  tocMajorVersionDescriptions?: TocMajorVersionDescription[];
  tocMinorVersion?: number;
  isBackupVersion: boolean;
  originHierarchyId?: string;
  comments: CommentReadDto[];
  hierarchyLinks: HierarchyLink[];
  clinicalLibrary?: ClinicalLibraryReadDto;
  hasClinicalLibrary: boolean;
  arsEnabled?: boolean;
  analysisMethods?: ARS.AnalysisMethod[];
  analysisSets?: ARS.AnalysisSet[];
  dataSubsets?: ARS.DataSubset[];
  groupingFactors?: ARS.GroupingFactor[];
}

/**
 * Type guard that asserts whether a value conforms to the HierarchyReadDto shape.
 *
 * @param obj - The object to check
 * @returns boolean
 */
export const isHierarchyReadDto = (obj: unknown): obj is HierarchyReadDto => {
  // Check object
  if (!obj || typeof obj !== "object") return false;
  // Check basic properties
  const chkObj = obj as HierarchyReadDto;
  if (typeof chkObj.id !== "string") return false;
  if (typeof chkObj.parentTag !== "string") return false;
  if (typeof chkObj.name !== "string") return false;
  if (typeof chkObj.parentId !== "string") return false;
  if (typeof chkObj.taPrefix !== "string") return false;
  if (typeof chkObj.projectCode !== "string") return false;
  if (typeof chkObj.dNumber !== "string") return false;
  if (typeof chkObj.description !== "string") return false;
  if (chkObj.phase && typeof chkObj.phase !== "string") return false;
  if (typeof chkObj.hierarchyTag !== "string") return false;
  if (chkObj.reportType && typeof chkObj.reportType !== "string") return false;
  if (chkObj.status && typeof chkObj.status !== "string") return false;
  if (chkObj.studyStandardsDate && typeof chkObj.studyStandardsDate !== "string") return false;
  if (chkObj.tocMajorVersion && typeof chkObj.tocMajorVersion !== "number") return false;
  if (chkObj.tocMinorVersion && typeof chkObj.tocMinorVersion !== "number") return false;
  if (typeof chkObj.isBackupVersion !== "boolean") return false;
  if (chkObj.originHierarchyId && typeof chkObj.originHierarchyId !== "string") return false;
  if (typeof chkObj.hasClinicalLibrary !== "boolean") return false;
  if (
    chkObj.analysisMethods !== undefined &&
    (!Array.isArray(chkObj.analysisMethods) ||
      chkObj.analysisMethods.some((x) => !isAnalysisMethod(x)))
  )
    return false;
  // Check enums
  if (!Object.values(eHierarchy).includes(chkObj.type)) return false;
  if (!Object.values(eHierarchyLevel).includes(chkObj.level)) return false;
  // Check arrays
  if (
    !Array.isArray(chkObj.lists) ||
    chkObj.lists.some((x) => !isAioExternalReplacements(x, isIEditorV3))
  )
    return false;
  if (
    !Array.isArray(chkObj.domainReplacements) ||
    chkObj.domainReplacements.some((x) => !isDomainReplacements(x))
  )
    return false;
  if (
    !Array.isArray(chkObj.rowGroups) ||
    chkObj.rowGroups.some((x) => !isAitRowGroupData(x, isIEditorV3, true))
  )
    return false;
  if (!Array.isArray(chkObj.sections) || chkObj.sections.some((x) => !isITocSection(x)))
    return false;
  if (
    !Array.isArray(chkObj.singles) ||
    chkObj.singles.some((x) => !isAioExternalSingle(x, isIEditorV3))
  )
    return false;
  if (!Array.isArray(chkObj.domainList) || chkObj.domainList.some((x) => typeof x !== "string"))
    return false;
  if (!Array.isArray(chkObj.children) || chkObj.children.some((x) => !isHierarchyChildReadDto(x)))
    return false;
  if (!Array.isArray(chkObj.history) || chkObj.history.some((x) => !isHierarchyHistoryDto(x)))
    return false;
  if (
    chkObj.tocMajorVersionDescriptions &&
    (!Array.isArray(chkObj.tocMajorVersionDescriptions) ||
      chkObj.tocMajorVersionDescriptions.some((x) => !isTocMajorVersionDescription(x)))
  )
    return false;
  if (!Array.isArray(chkObj.comments) || chkObj.comments.some((x) => !isCommentReadDto(x)))
    return false;
  if (
    !Array.isArray(chkObj.hierarchyLinks) ||
    chkObj.hierarchyLinks.some((x) => !isHierarchyLink(x))
  )
    return false;
  // Check optional objects
  if (chkObj.clinicalLibrary && !isClinicalLibraryReadDto(chkObj.clinicalLibrary)) return false;
  // Everything is good
  return true;
};
